/**
 * A stopped turn's record on its message: `finishedAt` + `aborted`, as in
 * `turnTiming.ts`. No runtime imports, so it can be tested under plain node.
 */
import type { UIMessage } from 'ai';
import type { TurnTimingMetadata } from './turnTiming';

type Timing = Record<string, unknown> & TurnTimingMetadata;

function timingOf(message: UIMessage): Timing {
  const m = message.metadata;
  return m && typeof m === 'object' ? (m as Timing) : {};
}

/** Stamp the turn's response (the last message, when it is the assistant's) as stopped now. */
export function stampAbortedTurn(messages: UIMessage[], abortedAt: string = new Date().toISOString()): UIMessage[] {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return messages;
  const prev = timingOf(last);
  if (prev.aborted === true && typeof prev.finishedAt === 'string') return messages;
  return [...messages.slice(0, -1), { ...last, metadata: { ...prev, finishedAt: abortedAt, aborted: true } }];
}

/**
 * `messages` with the stops recorded in `current` kept. A write can carry an
 * older copy of a stopped turn: assistant-ui's `cancelRun` writes the messages
 * back as they were when Stop was pressed, a tick after `stampAbortedTurn`.
 */
export function keepAbortedTurns(messages: UIMessage[], current: readonly UIMessage[]): UIMessage[] {
  const stops = new Map<string, string | undefined>();
  for (const m of current) {
    const t = timingOf(m);
    if (t.aborted === true) stops.set(m.id, t.finishedAt);
  }
  if (stops.size === 0) return messages;
  let changed = false;
  const kept = messages.map((m) => {
    const prev = timingOf(m);
    if (!stops.has(m.id) || prev.aborted === true) return m;
    changed = true;
    return { ...m, metadata: { ...prev, finishedAt: stops.get(m.id), aborted: true } };
  });
  return changed ? kept : messages;
}
